/**
 * Chart image / PDF export. Composites the live uPlot canvas with the chart's
 * DOM-side context (series legend, color legend, filter summary, changeover
 * legend, fit readout) onto an enlarged, themed canvas via `composeExportCanvas`,
 * then saves it as PNG/JPG (anchor download) or PDF (jsPDF, lazy-loaded chunk per
 * §2). The export button that calls this is gated by the `chartReady` atom.
 *
 * The canvas lives under `runtime.rootEl` (the `#chart-root` uPlot owns); we never
 * reach into `document` for it.
 */

import { runtime } from './plot-runtime.ts';
import { selectedShip } from '../../state/store.ts';
import { showToast } from '../../ui/toast.ts';
import { composeExportCanvas } from './render/export-compose.ts';

export type ImageFormat = 'png' | 'jpg' | 'pdf';

/** `{ship}_{YYYY-MM-DD-HH-MM-SS}` - the shared filename stem (no extension). */
function fileStem(): string {
  const ship = selectedShip.value || 'chart';
  const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  return `${ship}_${ts}`;
}

export async function exportChartImage(format: ImageFormat): Promise<void> {
  const root = runtime.rootEl;
  const srcCanvas = root?.querySelector('canvas') ?? null;
  if (!srcCanvas) {
    showToast('error', 'No chart canvas found');
    return;
  }

  // Composite the canvas with its DOM-side legends/strips over a themed
  // background so PNG/JPG/PDF are a complete, non-transparent snapshot.
  let tmp: HTMLCanvasElement;
  try {
    tmp = composeExportCanvas(srcCanvas);
  } catch {
    showToast('error', 'Could not create export canvas');
    return;
  }
  const w = tmp.width;
  const h = tmp.height;

  const stem = fileStem();

  if (format === 'pdf') {
    await exportPdf(tmp, w, h, stem);
    return;
  }

  const mime = format === 'jpg' ? 'image/jpeg' : 'image/png';
  const ext = format === 'jpg' ? 'jpg' : 'png';
  const link = document.createElement('a');
  link.download = `${stem}.${ext}`;
  link.href = tmp.toDataURL(mime, 0.95);
  link.click();
}

async function exportPdf(
  canvas: HTMLCanvasElement,
  w: number,
  h: number,
  stem: string,
): Promise<void> {
  let jsPDF: typeof import('jspdf').jsPDF;
  try {
    ({ jsPDF } = await import('jspdf'));
  } catch {
    showToast('error', 'PDF library not loaded - check network access');
    return;
  }
  const imgData = canvas.toDataURL('image/png');
  // Fit the page to the canvas pixel size so the chart keeps its aspect ratio
  // without scaling artifacts; viewers zoom as usual.
  const orientation = w >= h ? 'landscape' : 'portrait';
  const pdf = new jsPDF({ orientation, unit: 'px', format: [w, h], hotfixes: ['px_scaling'] });
  pdf.addImage(imgData, 'PNG', 0, 0, w, h, undefined, 'FAST');
  pdf.save(`${stem}.pdf`);
}
